import { ReactElement, useCallback } from "react";
import {
  FileUploadConfig,
  isFileUploadValidationError,
  ProjectDecisionAttachment,
  UploadedAttachment,
  validateFiles,
} from "../fileUpload/fileUploadDomain";
import { AlertMessageText } from "../stores/AlertMessageStore";
import { UploadFilesPanel } from "./UploadFilesPanel";
import { createFileTypeValidationErrorMessage } from "../fileUpload/fileUploadErrorMessages";
import { Box, VBox } from "./Box";
import { IconButton } from "./Button";
import { Icon } from "./Icon";
import { observer } from "mobx-react";
import { logError } from "../utils/log";
import { Checkbox } from "./Checkbox";
import { stopPropagation } from "../utils/events";

import { useDispatch, useTranslation } from "../stores/RootStoreContext";
import { DCEvent } from "../events/eventsDomain";
import { DocumentType, getDocumentLabel } from "../common/documents";

type AttachmentListItemProps = {
  attachment: UploadedAttachment;
  checked?: boolean;
  isDeleting?: boolean;
  onDelete?: (id: string) => void;
  onOpen: ((id: string) => void) | DCEvent[];
  onToggle?: (id: string) => void;
};

export const AttachmentListItem = observer(
  ({ attachment, onToggle, checked, isDeleting, onDelete, onOpen }: AttachmentListItemProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    return (
      <VBox className="tw-truncate">
        <Box
          bg
          hoverBg
          rounded
          border
          alignItems={"center"}
          paddingLeft={"md"}
          paddingRight={"sm"}
          paddingY={onDelete ? "sm" : "md"}
          htmlProps={{
            onClick: () =>
              isDeleting
                ? undefined
                : Array.isArray(onOpen)
                  ? dispatch(onOpen)
                  : onOpen(attachment.id),
            tabIndex: 0,
          }}
          className={"cursor-pointer tw-truncate"}
        >
          <Icon name={"paperclip"} className={"subtle"} />
          <span className="tw-truncate">{attachment.filename}</span>
          {onDelete && (
            <Box marginLeft={"auto"}>
              <IconButton
                icon={"x"}
                tooltip={t("delete")}
                color="gray"
                onClick={stopPropagation(() => onDelete(attachment.id))}
                loading={isDeleting}
              />
            </Box>
          )}
          {onToggle && (
            <Box marginLeft={"auto"} padding={"sm"}>
              <Checkbox checked={checked} onChange={() => onToggle(attachment.id)} />
            </Box>
          )}
        </Box>
      </VBox>
    );
  },
);

export type AttachmentPanelProps = {
  fileUploadConfig?: FileUploadConfig;
  uploadedAttachments: UploadedAttachment[];
  includeAttachments?: UploadedAttachment[];
  onIncludeAttachment?: (id: string) => void;
  isUploading?: boolean;
  isDeleting?: (id: string) => boolean;
  onUpload?: (files: File[]) => void;
  onInvalidFile?: (errorMessage: AlertMessageText) => void;
  onOpen: (id: string) => void;
  onDelete?: (id: string) => void;
  title?: string | ReactElement | boolean;
  className?: string;
};

export type DecisionAttachmentPanelProps = {
  fileUploadConfig?: FileUploadConfig;
  uploadedAttachments: ProjectDecisionAttachment[];
  includeAttachments?: ProjectDecisionAttachment[];
  currentDocumentTypes: DocumentType[];
  onIncludeAttachment?: (id: string) => void;
  isUploading?: boolean;
  isDeleting?: (id: string) => boolean;
  onUpload?: (files: File[]) => void;
  onInvalidFile?: (errorMessage: AlertMessageText) => void;
  onOpen: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleDocumentType?: (
    attachment: ProjectDecisionAttachment,
    documentType: DocumentType,
  ) => void;
  title?: string | ReactElement | boolean;
  className?: string;
  isLocked: boolean;
};

export const AttachmentsPanel = observer(
  ({
    fileUploadConfig,
    uploadedAttachments,
    includeAttachments = [],
    onIncludeAttachment,
    isUploading,
    isDeleting,
    onUpload,
    onInvalidFile,
    onOpen,
    onDelete,
    title,
    className,
  }: AttachmentPanelProps) => {
    const { t } = useTranslation();

    const handleUpload = useCallback(
      (files: File[]) => {
        if (files.length && !isUploading && fileUploadConfig) {
          validateFiles(files, fileUploadConfig)
            .then(onUpload)
            .catch((err) => {
              logError(err, "Invalid attachments files");
              if (isFileUploadValidationError(err)) {
                onInvalidFile?.(createFileTypeValidationErrorMessage(err));
              }
            });
        }
      },
      [isUploading, onInvalidFile, onUpload, fileUploadConfig],
    );

    return (
      <VBox className={className}>
        {title !== false && (
          <div>
            {title === true || title === undefined
              ? `${t("attachments")} (${uploadedAttachments.length})`
              : title}
          </div>
        )}
        {includeAttachments &&
          onIncludeAttachment &&
          includeAttachments.map((attachment) => (
            <AttachmentListItem
              attachment={attachment}
              key={attachment.id}
              onOpen={onOpen}
              onToggle={onIncludeAttachment}
              checked={uploadedAttachments.includes(attachment)}
            />
          ))}
        {uploadedAttachments
          .filter((d) => !includeAttachments.includes(d))
          .map((attachment) => (
            <AttachmentListItem
              attachment={attachment}
              key={attachment.id}
              isDeleting={isDeleting?.(attachment.id)}
              onOpen={onOpen}
              onDelete={onDelete}
            />
          ))}
        {onUpload && fileUploadConfig && (
          <UploadFilesPanel
            fileUploadConfig={fileUploadConfig}
            isUploading={isUploading}
            onUpload={handleUpload}
          />
        )}
      </VBox>
    );
  },
);

export const DecisionAttachmentsPanel = observer(
  ({
    fileUploadConfig,
    uploadedAttachments,
    includeAttachments = [],
    currentDocumentTypes,
    onIncludeAttachment,
    isUploading,
    isDeleting,
    onUpload,
    onInvalidFile,
    onOpen,
    onDelete,
    onToggleDocumentType,
    title,
    className,
    isLocked,
  }: DecisionAttachmentPanelProps) => {
    const { t } = useTranslation();

    const handleUpload = useCallback(
      (files: File[]) => {
        if (files.length && !isUploading && fileUploadConfig) {
          validateFiles(files, fileUploadConfig)
            .then(onUpload)
            .catch((err) => {
              logError("Invalid attachments err", files);
              if (isFileUploadValidationError(err)) {
                onInvalidFile?.(createFileTypeValidationErrorMessage(err));
              }
            });
        }
      },
      [isUploading, onInvalidFile, onUpload, fileUploadConfig],
    );

    return (
      <VBox className={className}>
        {title !== false && (
          <div>
            {title === true || title === undefined
              ? `${t("attachments")} (${uploadedAttachments.length})`
              : title}
          </div>
        )}
        {includeAttachments &&
          onIncludeAttachment &&
          includeAttachments.map((attachment) => (
            <VBox key={attachment.id} justifyContent={"start"}>
              <AttachmentListItem
                attachment={attachment}
                onOpen={onOpen}
                onToggle={onIncludeAttachment}
                checked={uploadedAttachments.includes(attachment)}
              />
              <div>{t("decisions.freetext.attachment.choose-documents")}</div>
              {currentDocumentTypes.map((docType) => (
                <Box key={docType} paddingX>
                  <Checkbox
                    label={t(getDocumentLabel(docType))}
                    onChange={() => onToggleDocumentType?.(attachment, docType)}
                    checked={attachment.selectedDocuments?.includes(docType)}
                    disabled={isLocked}
                  />
                </Box>
              ))}
            </VBox>
          ))}
        {uploadedAttachments
          .filter((d) => !includeAttachments.includes(d))
          .map((attachment) => (
            <VBox key={attachment.id} justifyContent={"start"}>
              <AttachmentListItem
                attachment={attachment}
                isDeleting={isDeleting?.(attachment.id)}
                onOpen={onOpen}
                onDelete={onDelete}
              />
              <div>{t("decisions.freetext.attachment.choose-documents")}</div>
              {currentDocumentTypes.map((docType) => (
                <Box key={docType} paddingX>
                  <Checkbox
                    label={t(getDocumentLabel(docType))}
                    onChange={() => onToggleDocumentType?.(attachment, docType)}
                    checked={attachment.selectedDocuments?.includes(docType)}
                    disabled={isLocked}
                  />
                </Box>
              ))}
            </VBox>
          ))}
        {onUpload && fileUploadConfig && (
          <UploadFilesPanel
            fileUploadConfig={fileUploadConfig}
            isUploading={isUploading}
            onUpload={handleUpload}
          />
        )}
      </VBox>
    );
  },
);
